import {
    AfterContentInit,
    Component,
    ContentChild,
    ContentChildren,
    Directive,
    ElementRef,
    EventEmitter,
    forwardRef,
    HostBinding,
    Input,
    OnChanges,
    Output,
    QueryList,
    Renderer,
    SimpleChange,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {Observable} from 'rxjs/Observable';
import {coerceBooleanProperty} from '../../../../common/value/coercion/boolean-coercion.util';
import {Error} from '../../../../common/error/error.class';
import {noop} from '../../../../common/functions/functions.util';

export const MY_INPUT_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => InputComponent),
    multi: true
};

// Invalid input type. Using one of these will throw an InputUnsupportedTypeError.
const MY_INPUT_INVALID_INPUT_TYPE = [
    'file',
    'radio',
    'checkbox',
];


let nextUniqueId = 0;


export class InputPlaceholderConflictError extends Error {
    constructor() {
        super('Placeholder attribute and child element were both specified.');
    }
}

export class InputUnsupportedTypeError extends Error {
    constructor(type: string) {
        super(`Input type "${type}" isn't supported by my-input.`);
    }
}

export class InputDuplicatedHintError extends Error {
    constructor(align: string) {
        super(`A hint was already declared for 'align="${align}"'.`);
    }
}


/**
 * The placeholder directive. The content can declare this to implement more
 * complex placeholders.
 */
@Directive({
    selector: 'my-placeholder'
})
export class PlaceholderDirective {
}


/** The hint directive, used to tag content as hint labels (going under the input). */
@Directive({
    selector: 'my-hint',
    host: {
        '[class.my-right]': 'align == "end"',
        '[class.my-hint]': 'true'
    }
})
export class HintDirective {
    // Whether to align the hint label at the start or end of the line.
    @Input() align: 'start' | 'end' = 'start';
}

/**
 * Component that represents a text input. It encapsulates the <input> HTMLElement and
 * improve on its behaviour, along with styling it according to the Material Design.
 */
@Component({
    selector: 'my-input, my-textarea',
    template: require('./input.component.html'),
    styles: [require('./input.component.scss')],
    providers: [MY_INPUT_CONTROL_VALUE_ACCESSOR],
    host: {'(click)': 'focus()'},
    encapsulation: ViewEncapsulation.None,
})
export class InputComponent implements ControlValueAccessor, AfterContentInit, OnChanges {
    @HostBinding('class') @Input() style?: 'admin';
    private _focused: boolean = false;
    private _value: any = '';

    /** Callback registered via registerOnTouched (ControlValueAccessor) */
    private _onTouchedCallback: () => void = noop;
    /** Callback registered via registerOnChange (ControlValueAccessor) */
    private _onChangeCallback: (_: any) => void = noop;

    /**
     * Aria related inputs.
     */
    @Input('aria-label') ariaLabel: string;
    @Input('aria-labelledby') ariaLabelledBy: string;

    private _ariaDisabled: boolean;
    private _ariaRequired: boolean;
    private _ariaInvalid: boolean;

    @Input('aria-disabled')
    get ariaDisabled(): boolean {
        return this._ariaDisabled;
    }

    set ariaDisabled(value) {
        this._ariaDisabled = coerceBooleanProperty(value);
    }

    @Input('aria-required')
    get ariaRequired(): boolean {
        return this._ariaRequired;
    }

    set ariaRequired(value) {
        this._ariaRequired = coerceBooleanProperty(value);
    }

    @Input('aria-invalid')
    get ariaInvalid(): boolean {
        return this._ariaInvalid;
    }

    set ariaInvalid(value) {
        this._ariaInvalid = coerceBooleanProperty(value);
    }

    @Input() textMask: (string | RegExp)[];
    @Input() textMaskGuide: boolean = true;

    /**
     * Content directives.
     */
    @ContentChild(PlaceholderDirective) _placeholderChild: PlaceholderDirective;
    @ContentChildren(HintDirective) _hintChildren: QueryList<HintDirective>;

    /** Readonly properties. */
    get focused() {
        return this._focused;
    }

    get empty() {
        return (this._value == null || this._value === '') && this.type !== 'date';
    }

    get characterCount(): number {
        return this.empty ? 0 : ('' + this._value).length;
    }

    get inputId(): string {
        return `${this.id}-input`;
    }

    /**
     * Bindings.
     */
    @Input() align: 'start' | 'end' = 'start';
    @Input() dividerColor: 'primary' | 'accent' | 'warn' = 'primary';
    @Input() hintLabel: string = '';

    @Input() autocomplete: string;
    @Input() autocorrect: string;
    @Input() autocapitalize: string;
    @Input() id: string = `my-input-${nextUniqueId++}`;
    @Input() list: string | null = null;
    @Input() max: string | number | null = null;
    @Input() maxlength: number | null = null;
    @Input() min: string | number | null = null;
    @Input() minlength: number | null = null;
    @Input() placeholder: string | null = null;
    @Input() step: number | null = null;
    @Input() tabindex: number | null = null;
    @Input() type: string | null = 'text';
    @Input() name: string | null = null;

    // textarea-specific
    @Input() rows: number | null = null;
    @Input() cols: number | null = null;
    @Input() wrap: 'soft' | 'hard' | null = null;
    @Input() maxRows: number | null = null;
    @Input() minRows: number | null = null;

    // typeahead
    @Input() typeahead: Array<string>;
    @Input() typeaheadFromStart: Boolean = false;
    @Input() typeaheadRestricted: Boolean = false;

    @Input() errorMessage: string;

    @Input() errorMessagePosition: string = 'right';

    search = (text$: Observable<string>) =>
        text$
            .debounceTime(200)
            .distinctUntilChanged()
            .map(term => term.length < 1 ? []
                : this.typeahead.filter(v => new RegExp((this.typeaheadFromStart ? '^' : '') + term, 'gi').test(v)).splice(0, 10));

    private _floatingPlaceholder: boolean = true;
    private _autofocus: boolean = false;
    private _disabled: boolean = false;
    private _readonly: boolean = false;
    private _required: boolean = false;
    private _spellcheck: boolean = false;

    @Input()
    get floatingPlaceholder(): boolean {
        return this._floatingPlaceholder;
    }

    set floatingPlaceholder(value) {
        this._floatingPlaceholder = coerceBooleanProperty(value);
    }

    @Input()
    get autofocus(): boolean {
        return this._autofocus;
    }

    set autofocus(value) {
        this._autofocus = coerceBooleanProperty(value);
    }

    @Input()
    get disabled(): boolean {
        return this._disabled;
    }

    set disabled(value) {
        this._disabled = coerceBooleanProperty(value);
    }

    @Input()
    get readonly(): boolean {
        return this._readonly;
    }

    set readonly(value) {
        this._readonly = coerceBooleanProperty(value);
    }

    @Input()
    get required(): boolean {
        return this._required;
    }

    set required(value) {
        this._required = coerceBooleanProperty(value);
    }

    @Input()
    get spellcheck(): boolean {
        return this._spellcheck;
    }

    set spellcheck(value) {
        this._spellcheck = coerceBooleanProperty(value);
    }


    private _blurEmitter: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();
    private _focusEmitter: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();

    @Output('blur')
    get onBlur(): Observable<FocusEvent> {
        return this._blurEmitter.asObservable();
    }

    @Output('focus')
    get onFocus(): Observable<FocusEvent> {
        return this._focusEmitter.asObservable();
    }

    get value(): any {
        return this._value;
    };

    @Input() set value(v: any) {
        v = this._convertValueForInputType(v);
        if (v !== this._value) {
            this._value = v;
            this._onChangeCallback(v);
        }
    }

    // This is to remove the `align` property of the `my-input` itself. Otherwise HTML5
    // might place it as RTL when we don't want to. We still want to use `align` as an
    // Input though, so we use HostBinding.
    @HostBinding('attr.align') get _align(): any {
        return null;
    }


    @ViewChild('input') _inputElement: ElementRef;

    _elementType: 'input' | 'textarea';

    constructor(elementRef: ElementRef, private _renderer: Renderer) {
        // Set the element type depending on normalized selector used(my-input / my-textarea)
        this._elementType = elementRef.nativeElement.nodeName.toLowerCase() === 'my-input' ?
            'input' :
            'textarea';
    }

    /** Set focus on input */
    focus() {
        this._renderer.invokeElementMethod(this._inputElement.nativeElement, 'focus');
    }

    _handleFocus(event: FocusEvent) {
        this._focused = true;
        this._focusEmitter.emit(event);
    }

    _handleBlur(event: FocusEvent) {
        this._focused = false;
        this._onTouchedCallback();
        this._blurEmitter.emit(event);
        //TODO Temporary fix for https://github.com/ng-bootstrap/ng-bootstrap/issues/1276
        //relevant PR: https://github.com/ng-bootstrap/ng-bootstrap/pull/1468/files#diff-4b999ef7b06784feb23d420f3d909d1aR202
        setTimeout(() => {
            if (this.typeahead && this.typeaheadRestricted && !this.value) {
                this._inputElement.nativeElement.value = '';
            }
        }, 200);
    }

    _handleChange = () => {
        if (this.textMask) this.value = this._inputElement.nativeElement.value;
        this._onTouchedCallback();
    }

    _hasPlaceholder(): boolean {
        return !!this.placeholder || this._placeholderChild != null;
    }

    /**
     * Implemented as part of ControlValueAccessor.
     * TODO: internal
     */
    writeValue(value: any) {
        //fix for TextMask not clearing input field completely when set to empty string
        if (this.textMask !== undefined && value === undefined) {
            this._inputElement.nativeElement.value = '';
        }

        this._value = value;
    }

    /**
     * Implemented as part of ControlValueAccessor.
     * TODO: internal
     */
    registerOnChange(fn: any) {
        this._onChangeCallback = fn;
    }

    /**
     * Implemented as part of ControlValueAccessor.
     * TODO: internal
     */
    registerOnTouched(fn: any) {
        this._onTouchedCallback = fn;
    }

    /**
     * Implemented as a part of ControlValueAccessor.
     */
    setDisabledState(isDisabled: boolean) {
        this.disabled = isDisabled;
    }

    /** TODO: internal */
    ngAfterContentInit() {
        this._validateConstraints();

        // Trigger validation when the hint children change.
        this._hintChildren.changes.subscribe(() => {
            this._validateConstraints();
        });
    }

    /** TODO: internal */
    ngOnChanges(changes: { [key: string]: SimpleChange }) {
        this._validateConstraints();
    }

    /**
     * Convert the value passed in to a value that is expected from the type of the my-input.
     * This is normally performed by the *_VALUE_ACCESSOR in forms, but since the type is bound
     * on our internal input it won't work locally.
     * @private
     */
    private _convertValueForInputType(v: any): any {
        switch (this.type) {
            case 'number':
                return parseFloat(v);
            default:
                return v;
        }
    }

    /**
     * Ensure that all constraints defined by the API are validated, or throw errors otherwise.
     * Constraints for now:
     *   - placeholder attribute and <my-placeholder> are mutually exclusive.
     *   - type attribute is not one of the forbidden types (see constant at the top).
     *   - Maximum one of each `<my-hint>` alignment specified, with the attribute being
     *     considered as align="start".
     * @private
     */
    private _validateConstraints() {
        if (this.placeholder !== '' && this.placeholder != null && this._placeholderChild != null) {
            throw new InputPlaceholderConflictError();
        }
        if (this.type != null && MY_INPUT_INVALID_INPUT_TYPE.indexOf(this.type) !== -1) {
            throw new InputUnsupportedTypeError(this.type);
        }

        if (this._hintChildren) {
            // Validate the hint labels.
            let startHint: HintDirective | null = null;
            let endHint: HintDirective | null = null;
            this._hintChildren.forEach((hint: HintDirective) => {
                if (hint.align === 'start') {
                    if (startHint || this.hintLabel) {
                        throw new InputDuplicatedHintError('start');
                    }
                    startHint = hint;
                } else if (hint.align === 'end') {
                    if (endHint) {
                        throw new InputDuplicatedHintError('end');
                    }
                    endHint = hint;
                }
            });
        }
    }

    preventEventPropagation(event: KeyboardEvent): void {
        event.stopPropagation();
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/tslint-loader!./src/app/components/forms/controls/input/input.component.ts